import { forwardRef, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Icon } from './icons';
import { IconButton } from './primitives';

/**
 * Fields (FRONTEND-AUDIT §D, §E): the search box and the filter chip every
 * list uses, promoted from `Table` in `ui/records.tsx`, and the controls a
 * form is made of. A screen draws none of these itself.
 */

/** A search box with its clear button, 44px tall, as every list has it. */
export function SearchField({ label, value, onChange, width = 'max-w-[360px]' }: { label: string; value: string; onChange: (value: string) => void; width?: string }) {
  return (
    <label className={`flex min-h-tap w-full ${width} flex-[1_1_240px] items-center gap-2.5 border border-control bg-raised px-3.5 text-meta focus-within:border-strong`}>
      <Icon glyph="search" size={14} />
      <input
        type="search"
        aria-label={label}
        placeholder={label}
        className="min-w-0 flex-1 border-0 bg-transparent py-2.5 text-dense text-ink outline-none placeholder:text-meta"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <IconButton label="Clear search" glyph="close" ghost onClick={() => onChange('')} />
      )}
    </label>
  );
}

/** One filter value, on or off; ink when on. */
export function FilterChip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`min-h-tap min-w-tap border px-[11px] text-label ${
        on ? 'border-ink bg-ink text-page' : 'border-control bg-page text-secondary hover:border-strong hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/** One tab of a `role=tablist`; the one chosen has the heavier rule beneath it. */
export function Tab({
  selected,
  onClick,
  fill,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  /** Share the row with its siblings equally. */
  fill?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={`-mb-px min-h-tap min-w-tap border-b-2 px-4 text-label uppercase tracking-label ${fill ? 'flex-1' : ''} ${
        selected ? 'border-strong text-ink' : 'border-transparent text-meta hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * A multi-line field (FRONTEND-AUDIT §E): a send-back note, a reason, a
 * template, a draft. The size of what it holds is the caller's; the rule,
 * the ground, the type and the focus are the library's.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(function Textarea(
  { invalid, className = '', ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      // A caller's height replaces the default rather than competing with
      // it: two min-heights in one class list are decided by stylesheet
      // order, not by which was written last.
      className={`${/\bmin-h-/.test(className) ? '' : 'min-h-[88px]'} w-full rounded-brand border bg-raised p-3 text-body text-ink outline-none focus:border-strong ${invalid ? 'border-danger' : 'border-control'} ${className}`}
      {...rest}
    />
  );
});

/**
 * One of many (FRONTEND-AUDIT §E): a person, a company, a department, a
 * window of days. Native on purpose — a phone opens its own picker, and a
 * screen reader already knows it — styled to the library's field. Options
 * come as children or as `options`.
 */
export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean; options?: ReadonlyArray<{ value: string; label: string; disabled?: boolean }> }
>(function Select({ invalid, options, className = '', children, ...rest }, ref) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={`min-h-tap rounded-brand border bg-raised px-3 text-dense text-ink outline-none focus:border-strong disabled:opacity-40 ${invalid ? 'border-danger' : 'border-control'} ${className}`}
      {...rest}
    >
      {options
        ? options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))
        : children}
    </select>
  );
});
